import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import { colorForIndex } from "../helpers/chart"
import { formatDynamicsDateTime } from "../helpers/datetime"

Chart.register(...registerables)

export default class extends Controller {
  static targets = [
    "testSelect",
    "chartablePanel",
    "chartSection",
    "notChartablePanel",
    "notChartableMessage",
    "canvas",
    "chartWrap",
    "tableHead",
    "tableBody",
    "tableWrap",
    "szondiMobileWrap",
    "limitForm",
    "limitTestType",
    "attemptsCount",
    "rciSection",
    "rciList"
  ]

  static values = {
    payload: Object,
    initialCode: String
  }

  connect() {
    this.payload = this.normalizePayload(this.payloadValue)
    this.currentCode = this.initialCodeValue || this.payload.test_types[0]?.code
    this.mobileMediaQuery = window.matchMedia(`(max-width: ${768}px)`)
    this.onMobileChange = () => this.renderCurrent()
    this.mobileMediaQuery.addEventListener("change", this.onMobileChange)
    this.onWindowResize = this.debounce(() => this.resizeChart(), 150)
    window.addEventListener("resize", this.onWindowResize)
    if (this.hasChartWrapTarget) {
      this.resizeObserver = new ResizeObserver(() => this.resizeChart())
      this.resizeObserver.observe(this.chartWrapTarget)
    }
    this.renderCurrent()
  }

  disconnect() {
    this.destroyChart()
    this.mobileMediaQuery?.removeEventListener("change", this.onMobileChange)
    window.removeEventListener("resize", this.onWindowResize)
    this.resizeObserver?.disconnect()
  }

  selectTest(event) {
    const code = event.currentTarget.value
    if (!code || code === this.currentCode) return

    this.currentCode = code
    this.syncLimitTestType()
    this.renderCurrent()
  }

  submitLimit() {
    if (!this.hasLimitTestTypeTarget) return
    this.limitTestTypeTarget.value = this.currentCode || ""
    this.limitFormTarget.submit()
  }

  renderCurrent() {
    const entry = this.payload.test_types.find((row) => row.code === this.currentCode)
    if (!entry) return

    this.syncTestSelect(entry.code)
    this.syncLimitTestType()
    this.updateAttemptsCount(entry)
    if (entry.display_mode === "table_text") {
      this.showTableText(entry)
    } else if (entry.chartable) {
      this.showChartable(entry)
    } else {
      this.showNotChartable(entry)
    }
    this.renderRci(entry)
  }

  showTableText(entry) {
    this.chartablePanelTarget.classList.remove("is-hidden")
    this.notChartablePanelTarget.classList.add("is-hidden")
    if (this.hasChartSectionTarget) {
      this.chartSectionTarget.classList.add("is-hidden")
    }
    this.destroyChart()
    this.renderTable(entry)
  }

  showChartable(entry) {
    this.chartablePanelTarget.classList.remove("is-hidden")
    this.notChartablePanelTarget.classList.add("is-hidden")
    if (this.hasChartSectionTarget) {
      this.chartSectionTarget.classList.remove("is-hidden")
    }
    this.renderChart(entry)
    this.renderTable(entry)
  }

  showNotChartable(entry) {
    this.destroyChart()
    this.chartablePanelTarget.classList.add("is-hidden")
    this.notChartablePanelTarget.classList.remove("is-hidden")
    this.notChartableMessageTarget.textContent =
      entry.not_chartable_reason || this.payload.strings.not_chartable || ""
  }

  renderChart(entry) {
    this.destroyChart()
    const chartData = entry.chart_data || []
    if (!chartData.length) return

    const scaleOrder = entry.scale_order || []
    const scaleLabels = entry.scale_labels || {}
    const locale = this.payload.strings?.locale
    const labels = chartData.map(
      (row) => formatDynamicsDateTime(row.completed_at, locale) || row.label || row.date
    )
    const datasets = scaleOrder.map((code, index) => ({
      label: scaleLabels[code] || code,
      data: chartData.map((row) => (row.scales && row.scales[code]) ?? null),
      borderColor: colorForIndex(index),
      backgroundColor: colorForIndex(index),
      tension: 0.2,
      fill: false,
      spanGaps: true
    }))

    const yMax = entry.y_max || 24
    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: { boxWidth: 12 }
          }
        },
        scales: {
          y: { beginAtZero: true, max: yMax }
        }
      }
    })
    this.resizeChart()
  }

  renderRci(entry) {
    if (!this.hasRciSectionTarget || !this.hasRciListTarget) return
    const rows = entry.rci || []
    this.rciListTarget.replaceChildren()
    if (!rows.length) {
      this.rciSectionTarget.classList.add("is-hidden")
      return
    }
    this.rciSectionTarget.classList.remove("is-hidden")
    const reliableLabel = this.payload.strings?.rci_reliable || ""
    const labels = entry.scale_labels || {}
    rows.forEach((row) => {
      const li = document.createElement("li")
      const name = labels[row.scale] || row.scale
      const mark = row.reliable && reliableLabel ? ` — ${reliableLabel}` : ""
      li.textContent = `${name}: ${row.rci}${mark}`
      this.rciListTarget.appendChild(li)
    })
  }

  renderTable(entry) {
    if (entry.column_groups?.length) {
      this.renderGroupedTable(entry)
      return
    }

    this.renderFlatTable(entry)
  }

  renderFlatTable(entry) {
    this.clearGroupedTableMode()
    const scaleOrder = entry.scale_order || []
    const scaleLabels = entry.scale_labels || {}
    const dateLabel = this.payload.strings.date_time || "Date"
    const rows = entry.attempts_table || []

    const headRow = document.createElement("tr")
    const dateTh = document.createElement("th")
    dateTh.scope = "col"
    dateTh.textContent = dateLabel
    headRow.appendChild(dateTh)
    scaleOrder.forEach((code) => {
      const th = document.createElement("th")
      th.scope = "col"
      th.textContent = scaleLabels[code] || code
      headRow.appendChild(th)
    })
    this.tableHeadTarget.replaceChildren(headRow)

    const bodyFrag = document.createDocumentFragment()
    rows.forEach((row) => {
      const tr = document.createElement("tr")
      tr.appendChild(this.renderDateCell(row, dateLabel))
      scaleOrder.forEach((code) => {
        const td = document.createElement("td")
        td.dataset.label = scaleLabels[code] || code
        const value = row.scales && row.scales[code]
        td.textContent = value == null ? "—" : String(value)
        const title = row.scale_titles && row.scale_titles[code]
        if (title) td.title = title
        tr.appendChild(td)
      })
      bodyFrag.appendChild(tr)
    })
    this.tableBodyTarget.replaceChildren(bodyFrag)
  }

  renderGroupedTable(entry) {
    this.setGroupedTableMode()
    const groups = entry.column_groups
    const scaleOrder = entry.scale_order || groups.flatMap((group) => group.codes)
    const scaleLabels = entry.scale_labels || {}
    const dateLabel = this.payload.strings.date_time || "Date"
    const rows = entry.attempts_table || []

    const groupRow = document.createElement("tr")
    const dateTh = document.createElement("th")
    dateTh.rowSpan = 2
    dateTh.scope = "col"
    dateTh.textContent = dateLabel
    groupRow.appendChild(dateTh)

    groups.forEach((group) => {
      const th = document.createElement("th")
      th.colSpan = group.codes.length
      th.scope = "colgroup"
      th.className = "test-dynamics__plane-header"
      th.textContent = group.label
      groupRow.appendChild(th)
    })

    const factorRow = document.createElement("tr")
    groups.forEach((group) => {
      group.codes.forEach((code) => {
        const th = document.createElement("th")
        th.scope = "col"
        th.textContent = scaleLabels[code] || code
        factorRow.appendChild(th)
      })
    })

    this.tableHeadTarget.replaceChildren(groupRow, factorRow)

    const bodyFrag = document.createDocumentFragment()
    rows.forEach((row) => {
      const tr = document.createElement("tr")
      tr.appendChild(this.renderDateCell(row, dateLabel))
      scaleOrder.forEach((code) => {
        const td = document.createElement("td")
        td.dataset.label = `${scaleLabels[code] || code}`
        const value = row.scales && row.scales[code]
        td.textContent = value == null ? "—" : String(value)
        const title = row.scale_titles && row.scale_titles[code]
        if (title) td.title = title
        tr.appendChild(td)
      })
      bodyFrag.appendChild(tr)
    })

    if (entry.scale_summaries) {
      this.renderSummaryRows(entry, scaleOrder, scaleLabels, dateLabel).forEach((row) => {
        bodyFrag.appendChild(row)
      })
    }

    this.tableBodyTarget.replaceChildren(bodyFrag)
    this.renderSzondiMobileCards(entry)
  }

  setGroupedTableMode() {
    if (this.hasTableWrapTarget) {
      this.tableWrapTarget.classList.add("test-dynamics__table-wrap--grouped")
    }
  }

  clearGroupedTableMode() {
    if (this.hasTableWrapTarget) {
      this.tableWrapTarget.classList.remove("test-dynamics__table-wrap--grouped")
    }
    this.clearSzondiMobileCards()
  }

  renderSzondiMobileCards(entry) {
    if (!this.hasSzondiMobileWrapTarget) return

    this.szondiMobileWrapTarget.replaceChildren()
    if (!entry.scale_summaries || !this.isMobileViewport()) {
      this.szondiMobileWrapTarget.classList.add("is-hidden")
      return
    }

    this.szondiMobileWrapTarget.classList.remove("is-hidden")
    const scaleOrder = entry.scale_order || []
    const scaleLabels = entry.scale_labels || {}
    const rows = entry.attempts_table || []
    const strings = this.payload.strings || {}

    scaleOrder.forEach((code) => {
      const details = document.createElement("details")
      details.className = "test-dynamics__scale-card"
      details.open = scaleOrder.length <= 4

      const summary = document.createElement("summary")
      summary.className = "test-dynamics__scale-card-summary"
      summary.textContent = scaleLabels[code] || code
      details.appendChild(summary)

      const body = document.createElement("div")
      body.className = "test-dynamics__scale-card-body"

      rows.forEach((row) => {
        const rowDiv = document.createElement("div")
        rowDiv.className = "test-dynamics__scale-card-row"
        const label = document.createElement("span")
        label.textContent = this.formatRowCompletedAt(row)
        const value = document.createElement("span")
        const raw = row.scales && row.scales[code]
        value.textContent = raw == null ? "—" : String(raw)
        rowDiv.appendChild(label)
        rowDiv.appendChild(value)
        body.appendChild(rowDiv)
      })

      const counts = (entry.scale_summaries && entry.scale_summaries[code]) || {}
      const metrics = document.createElement("div")
      metrics.className = "test-dynamics__scale-card-metrics"
      metrics.textContent = [
        `${strings.summary_row_positive || "+"}: ${counts.positive ?? 0}`,
        `${strings.summary_row_negative || "−"}: ${counts.negative ?? 0}`,
        `${strings.summary_row_affect_positive || "Pos"}: ${counts.affect_positive_percent ?? 0}%`,
        `${strings.summary_row_affect_negative || "Neg"}: ${counts.affect_negative_percent ?? 0}%`
      ].join(" · ")
      body.appendChild(metrics)

      details.appendChild(body)
      this.szondiMobileWrapTarget.appendChild(details)
    })
  }

  clearSzondiMobileCards() {
    if (!this.hasSzondiMobileWrapTarget) return
    this.szondiMobileWrapTarget.replaceChildren()
    this.szondiMobileWrapTarget.classList.add("is-hidden")
  }

  isMobileViewport() {
    return window.matchMedia("(max-width: 768px)").matches
  }

  resizeChart() {
    if (this.chart) this.chart.resize()
  }

  debounce(fn, wait) {
    let timeoutId
    return (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  renderSummaryRows(entry, scaleOrder, scaleLabels, dateLabel) {
    const strings = this.payload.strings || {}
    const threshold = Number(strings.affect_high_threshold) || 65
    const metrics = [
      { key: "positive", label: strings.summary_row_positive || "+", format: "count" },
      { key: "negative", label: strings.summary_row_negative || "−", format: "count" },
      { key: "null", label: strings.summary_row_null || "0", format: "count" },
      { key: "ambivalent", label: strings.summary_row_ambivalent || "±", format: "count" },
      {
        key: "affect_positive",
        percentKey: "affect_positive_percent",
        label: strings.summary_row_affect_positive || "Positive affect",
        format: "affect_percent"
      },
      {
        key: "affect_negative",
        percentKey: "affect_negative_percent",
        label: strings.summary_row_affect_negative || "Negative affect",
        format: "affect_percent"
      }
    ]

    return metrics.map((metric) => {
      const tr = document.createElement("tr")
      tr.className = "test-dynamics__summary-row"

      const labelTd = document.createElement("td")
      labelTd.dataset.label = dateLabel
      labelTd.textContent = metric.label
      tr.appendChild(labelTd)

      scaleOrder.forEach((code) => {
        const td = document.createElement("td")
        td.dataset.label = scaleLabels[code] || code
        const counts = (entry.scale_summaries && entry.scale_summaries[code]) || {}

        if (metric.format === "affect_percent") {
          const percent = counts[metric.percentKey] ?? 0
          const count = counts[metric.key] ?? 0
          const total = counts.total ?? 0
          td.textContent = this.formatAffectCell(strings.summary_affect_cell, percent)
          td.title = this.formatAffectTitle(strings.summary_affect_title, count, total)
          if (percent > threshold) {
            td.classList.add("test-dynamics__summary-cell--high-affect")
          }
        } else {
          td.textContent = String(counts[metric.key] ?? 0)
        }

        tr.appendChild(td)
      })

      return tr
    })
  }

  formatAffectCell(template, percent) {
    const pattern = template || "%{percent}%"
    return pattern.replace("%{percent}", String(percent))
  }

  formatAffectTitle(template, count, total) {
    const pattern = template || "%{count} / %{total}"
    return pattern
      .replace("%{count}", String(count))
      .replace("%{total}", String(total))
  }

  updateAttemptsCount(entry) {
    if (!this.hasAttemptsCountTarget) return

    const template = this.payload.strings?.attempts_count || "%{shown} / %{total}"
    const shown = entry.attempts_shown_count ?? 0
    const total = entry.attempts_total_count ?? 0
    this.attemptsCountTarget.textContent = template
      .replace("%{shown}", String(shown))
      .replace("%{total}", String(total))
  }

  syncLimitTestType() {
    if (!this.hasLimitTestTypeTarget) return
    this.limitTestTypeTarget.value = this.currentCode || ""
  }

  syncTestSelect(activeCode) {
    if (!this.hasTestSelectTarget) return
    this.testSelectTarget.value = activeCode
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  formatRowCompletedAt(row) {
    const locale = this.payload.strings?.locale
    return formatDynamicsDateTime(row.completed_at, locale) || row.completed_at_label || ""
  }

  renderDateCell(row, dateLabel) {
    const dateTd = document.createElement("td")
    dateTd.dataset.label = dateLabel
    const label = this.formatRowCompletedAt(row)
    if (row.result_path) {
      const link = document.createElement("a")
      link.href = row.result_path
      link.textContent = label
      dateTd.appendChild(link)
    } else {
      dateTd.textContent = label
    }
    return dateTd
  }

  normalizePayload(raw) {
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw.replace(/&quot;/g, '"'))
      } catch {
        return { test_types: [], strings: {} }
      }
    }
    return raw || { test_types: [], strings: {} }
  }
}
